import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Activity, AlertTriangle, History, Share2 } from 'lucide-react';
import { listOffboardingStatus, getOffboardingStatusTrail, acknowledgeRoleAssignableRemoval } from '@/api/offboardingStatus';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { listOneDriveMigrations, getOneDriveSharedItems, triggerOneDriveMigration, runOneDriveMigrationCopy } from '@/api/onedriveMigration';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { OneDriveMigrationBadge } from '@/components/offboarding/OneDriveMigrationBadge';
import { formatDateTime, formatWallClock } from '@/lib/dateFormat';
import { useSort, sortRows } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import type {
  ManualGroupRemovalInfo,
  OffboardingOverallStatus,
  OffboardingSignalStatus,
  OffboardingStatusRow,
  OneDriveMigration,
} from '@/types/oscar';

const OVERALL_STATUSES: OffboardingOverallStatus[] = [
  'Needs Attention', 'In Progress', 'Complete', 'Not Started', 'Unknown',
];

export const overallBadge: Record<OffboardingOverallStatus, string> = {
  'Needs Attention': 'bg-red/10 text-red',
  'In Progress': 'bg-lightBlue/10 text-lightBlue',
  Complete: 'bg-green/10 text-green',
  'Not Started': 'bg-darkGrey/10 text-darkGrey',
  Unknown: 'bg-darkGrey/10 text-darkGrey',
};

const signalBadge: Record<OffboardingSignalStatus, string> = {
  SUCCESS: 'bg-green/10 text-green',
  PARTIAL: 'bg-orange/10 text-orange',
  FAILED: 'bg-red/10 text-red',
  SUPERSEDED: 'bg-darkGrey/10 text-darkGrey',
  SKIPPED: 'bg-darkGrey/5 text-darkGrey/70',
};

// Trail events may carry on-prem signal statuses (SUCCESS/…) or OneDrive
// migration statuses (Copied/Pending/…) — colour both vocabularies.
const trailStatusBadge: Record<string, string> = {
  ...signalBadge,
  Pending: 'bg-lightBlue/10 text-lightBlue',
  Copied: 'bg-green/10 text-green',
  Failed: 'bg-red/10 text-red',
  Skipped: 'bg-darkGrey/10 text-darkGrey/70',
};

// Best-effort pill colour for any status string (trail events can carry
// on-prem statuses beyond the four signals, e.g. WHATIF).
function statusClass(status: string | null): string {
  if (!status) return 'bg-darkGrey/10 text-darkGrey';
  return trailStatusBadge[status] ?? 'bg-darkGrey/10 text-darkGrey';
}

// The history trail merges three sources: on-prem AD/OnsiteLive, Oscar's
// email divert, and (added here) the OneDrive-to-Vault migration.
type TrailSource = 'OnPrem' | 'Oscar' | 'OneDrive';
interface TrailEventView {
  Source: TrailSource;
  Action: string;
  Status: string | null;
  Message: string | null;
  Timestamp: string;
}

const sourceLabel: Record<TrailSource, string> = {
  OnPrem: 'On-prem',
  Oscar: 'Oscar',
  OneDrive: 'OneDrive',
};

/**
 * Synthesises history events from a OneDrive migration row: the copy/migration
 * state itself, plus a "source cleared" event when PR-34's source-content delete
 * has run. Timestamps are Oscar-native (UTC) like the email-divert events, so
 * they render via {@link formatDateTime}. Returns [] when there is no migration.
 */
function buildOneDriveEvents(m: OneDriveMigration | undefined): TrailEventView[] {
  if (!m) return [];
  const events: TrailEventView[] = [];

  const copyMessage =
    m.Message ??
    (m.Status === 'Copied'
      ? `Copied${m.ItemsCopied != null ? ` ${m.ItemsCopied} item(s)` : ''}${m.VaultFolderPath ? ` to ${m.VaultFolderPath}` : ''}.`
      : m.Status === 'Pending'
        ? `Queued (${m.TriggerSource}); awaiting the next copy run.`
        : null);
  events.push({
    Source: 'OneDrive',
    Action: 'OneDrive migration',
    Status: m.Status,
    Message: copyMessage,
    Timestamp: m.CopyCompletedDate ?? m.CopyStartedDate ?? m.EligibleSince,
  });

  // Source-content delete (PR-34) — only surface it once it has actually run
  // (date set) or recorded why it didn't (message present). Off by default.
  if (m.SourceDeletedDate || m.SourceDeleteMessage) {
    events.push({
      Source: 'OneDrive',
      Action: 'OneDrive source cleared',
      Status: m.SourceDeletedDate ? 'SUCCESS' : null,
      Message:
        m.SourceDeleteMessage ??
        (m.SourceItemsDeleted != null ? `Deleted ${m.SourceItemsDeleted} source item(s).` : null),
      Timestamp: m.SourceDeletedDate ?? m.CopyCompletedDate ?? m.EligibleSince,
    });
  }

  return events;
}

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';

// Column sort accessors (keys match the SortHeader columnKey values).
const SORT_GET: Record<string, (r: OffboardingStatusRow) => unknown> = {
  username: (r) => r.Username,
  fullName: (r) => r.FullName,
  effectiveExpiry: (r) => r.EffectiveExpiryDateTime,
  overall: (r) => r.OverallStatus,
  phase1: (r) => r.Phase1Status,
  phase2: (r) => r.Phase2Status,
  onsiteLive: (r) => r.OnsiteLiveStatus,
  emailDivert: (r) => r.EmailDivertStatus,
  groups: (r) => r.GroupRemovalStatus,
};

/** A per-signal pill (SUCCESS/PARTIAL/FAILED/SUPERSEDED) or an em-dash when the
 * signal isn't recorded / isn't available. */
export function SignalPill({ status }: { status: OffboardingSignalStatus | null }) {
  if (!status) return <span className="text-darkGrey/50">—</span>;
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${signalBadge[status]}`}>
      {status}
    </span>
  );
}

/**
 * "Manual group removal" warning chip. Shown beside the Overall badge (where a
 * misleading "Complete" would otherwise hide it) while the cloud group-removal
 * runbook has finished but skipped role-assignable groups it cannot edit — the
 * leaver may still be in them (e.g. a Conditional Access exemption group).
 * Navy-on-tint with an orange icon: a call to action, deliberately not the red
 * "Needs Attention" failure state. Clears only on Acknowledge (History dialog).
 */
function ManualGroupBadge({ row }: { row: OffboardingStatusRow }) {
  if (!row.ManualGroupRemovalRequired) return null;
  const names = row.RoleAssignableSkipped.join(', ');
  return (
    <span
      title={`Still a member of role-assignable group(s) the runbook cannot edit: ${names}. Remove by hand, then Acknowledge in History.`}
      className="mt-1 flex w-fit items-center gap-1 rounded-full border border-orange/50 bg-navy/5 px-2 py-0.5 text-xs font-semibold text-navy"
    >
      <AlertTriangle className="h-3 w-3 text-orange" aria-hidden /> Manual group removal
    </span>
  );
}

/**
 * Offboarding Status (PR-32) — read-only visibility over the termination
 * pipeline. Each leaver's four signals in one place: AD Phase 1/2 and
 * OnsiteLive (on-prem, via the Oscar_AuditLog synonym) and the email divert
 * (Oscar-native, PR-30). No mutation UI. Click a row's History to see the full
 * audit trail (on-prem + Oscar events, with the message for each — e.g. why a
 * divert FAILED). If the on-prem phase source is unavailable those columns show
 * "—" and a page notice appears, but the email-divert column stays current.
 */
export function OffboardingStatus() {
  const { isAdmin } = useAuth();
  const [overall, setOverall] = useState<string>('');
  const [search, setSearch] = useState('');
  const [trailFor, setTrailFor] = useState<OffboardingStatusRow | null>(null);
  const [sharedFor, setSharedFor] = useState<OneDriveMigration | null>(null);

  const query = useQuery({
    queryKey: ['offboarding-status'],
    queryFn: ({ signal }) => listOffboardingStatus(signal),
  });

  // OneDrive migrations are keyed by PersonId; the leaver list is keyed by the
  // on-prem UserId. The only shared key is the username, so match on that
  // (case-insensitive). Leavers with no migration row show an em-dash.
  const odQuery = useQuery({
    queryKey: ['onedrive-migrations'],
    queryFn: ({ signal }) => listOneDriveMigrations(signal),
  });
  const odByUsername = new Map<string, OneDriveMigration>();
  for (const m of odQuery.data ?? []) {
    if (m.Username) odByUsername.set(m.Username.toLowerCase(), m);
  }
  const odFor = (r: OffboardingStatusRow): OneDriveMigration | undefined =>
    odByUsername.get((r.Username ?? '').toLowerCase());

  const { sortKey, sortDir, onSort } = useSort('effectiveExpiry', 'desc');
  const term = search.trim().toLowerCase();
  const items: OffboardingStatusRow[] = query.data?.Items ?? [];
  const filtered = items.filter((r) =>
    (!overall || r.OverallStatus === overall) &&
    (!term || [r.Username, r.FullName].some((v) => (v ?? '').toLowerCase().includes(term))),
  );
  const accessor = sortKey === 'onedrive'
    ? (r: OffboardingStatusRow) => odFor(r)?.Status ?? ''
    : (SORT_GET[sortKey] ?? ((r: OffboardingStatusRow) => r.EffectiveExpiryDateTime));
  const rows = sortRows(filtered, accessor, sortDir);

  return (
    <section>
      <header className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Offboarding</h1>
          <p className="mt-1 text-sm text-darkGrey/80">
            Termination pipeline status for each expiring user — AD Phase&nbsp;1/2 and OnsiteLive
            (on-premises) plus the Exchange email divert (handled by Oscar). Read-only; use
            <span className="font-semibold"> History</span> to see the full audit trail for a user.
          </p>
        </div>
        <Link
          to="/offboarding/jobs"
          className="inline-flex shrink-0 items-center gap-1.5 rounded border border-navy/30 bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg"
        >
          <Activity className="h-4 w-4" aria-hidden /> Background jobs
        </Link>
      </header>

      {query.data?.MiUnavailable && (
        <div role="status" className="mb-3 flex items-start gap-2 rounded border border-orange/30 bg-orange/10 px-3 py-2 text-xs text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span>
            AD / OnsiteLive (on-premises) status is temporarily unavailable, so those columns show
            &ldquo;—&rdquo;. Email divert status is Oscar-native and still current.
          </span>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Overall status
          <select aria-label="Filter by overall status" value={overall} onChange={(e) => setOverall(e.target.value)} className={inputCls}>
            <option value="">All</option>
            {OVERALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input
            aria-label="Search offboarding users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Username or full name"
            className={inputCls}
          />
        </label>
      </div>

      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <SortHeader label="Username" columnKey="username" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Full name" columnKey="fullName" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Effective expiry" columnKey="effectiveExpiry" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Overall" columnKey="overall" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Phase 1" columnKey="phase1" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Phase 2" columnKey="phase2" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="OnsiteLive" columnKey="onsiteLive" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Email divert" columnKey="emailDivert" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Groups" columnKey="groups" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="OneDrive" columnKey="onedrive" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <tr><td colSpan={11} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
            {query.isError && <tr><td colSpan={11} className="px-3 py-6 text-center text-red">Could not load offboarding status.</td></tr>}
            {!query.isLoading && !query.isError && rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-darkGrey/60">No offboarding users.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.UserId} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2 font-mono text-navy">{r.Username}</td>
                <td className="px-3 py-2 text-darkGrey/80">{r.FullName ?? '—'}</td>
                {/* Effective expiry is Sydney wall-clock (from OL_reportusers) — show verbatim, no UTC conversion. */}
                <td className="px-3 py-2 text-darkGrey/80">{formatWallClock(r.EffectiveExpiryDateTime)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${overallBadge[r.OverallStatus]}`}>
                    {r.OverallStatus}
                  </span>
                  <ManualGroupBadge row={r} />
                </td>
                <td className="px-3 py-2"><SignalPill status={r.Phase1Status} /></td>
                <td className="px-3 py-2"><SignalPill status={r.Phase2Status} /></td>
                <td className="px-3 py-2"><SignalPill status={r.OnsiteLiveStatus} /></td>
                <td className="px-3 py-2"><SignalPill status={r.EmailDivertStatus} /></td>
                <td className="px-3 py-2"><SignalPill status={r.GroupRemovalStatus} /></td>
                <td className="px-3 py-2">
                  <OneDriveCell migration={odFor(r)} isAdmin={isAdmin} onViewShared={setSharedFor} />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setTrailFor(r)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:underline"
                  >
                    <History className="h-3.5 w-3.5" aria-hidden /> History
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {trailFor && <TrailDialog row={trailFor} migration={odFor(trailFor)} onClose={() => setTrailFor(null)} />}
      {sharedFor && <SharedItemsDialog migration={sharedFor} onClose={() => setSharedFor(null)} />}
    </section>
  );
}

// mirrors OneDriveMigrationService.WaitDays (the copy-processor's AutoScan gate).
const WAIT_DAYS = 7;

/**
 * Whether the copy processor would act on this row now — Pending, and Manual
 * (immediate) or AutoScan past the 7-day wait. Mirrors the server's IsDue so the
 * "Run copy" affordance only appears when a run will actually process this
 * person (a not-yet-due AutoScan row would otherwise offer a no-op button).
 */
function isDuePending(m: OneDriveMigration): boolean {
  if (m.Status !== 'Pending') return false;
  if (m.TriggerSource === 'Manual') return true;
  return new Date(m.EligibleSince).getTime() <= Date.now() - WAIT_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Whether a copy is already running in the background for this row: still Pending
 * but with a CopyStartedDate (stamped only when an on-demand run hit its budget
 * and handed off to the scheduled run — retry/trigger clears it). Used to hide
 * the "Run copy" action so it isn't kicked again while a copy is in flight.
 */
export function isRunningInBackground(m: OneDriveMigration): boolean {
  return m.Status === 'Pending' && m.CopyStartedDate != null;
}

/**
 * OneDrive migration cell — the status badge plus a "Shared" button (opens the
 * shared-with manifest) and, for admins, an action to kick the copy processor
 * from here rather than waiting for the daily timer / Admin page:
 * "Re-run" on a Failed/Skipped row (re-queues the person, then runs the copy),
 * or "Run copy" on a due Pending row that is stuck waiting for a run (just runs
 * the copy — a Pending row is already queued, so it isn't reset). An em-dash
 * when the leaver has no migration row yet.
 */
function OneDriveCell({
  migration,
  isAdmin,
  onViewShared,
}: {
  migration: OneDriveMigration | undefined;
  isAdmin: boolean;
  onViewShared: (m: OneDriveMigration) => void;
}) {
  const queryClient = useQueryClient();
  const run = useMutation({
    mutationFn: async () => {
      // Any non-Pending row (Failed/Skipped/Copied) is reset to Pending first, then
      // the copy processor is kicked. A Pending row is already queued — just run.
      const needsReset = migration!.Status !== 'Pending';
      if (needsReset) await triggerOneDriveMigration(migration!.PersonId);
      return runOneDriveMigrationCopy();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onedrive-migrations'] }),
  });

  if (!migration) return <span className="text-darkGrey/50">—</span>;

  const running = isRunningInBackground(migration);
  // Admins can re-run any completed/terminal migration: Failed, Skipped, or Copied
  // (re-copy, and retry a source-delete that was blocked at verify).
  const canRerun = isAdmin && (migration.Status === 'Failed' || migration.Status === 'Skipped' || migration.Status === 'Copied');
  // Don't offer "Run copy" while a copy is already running in the background —
  // re-kicking it just restarts from scratch and confuses. Show "Running…" instead.
  const canRunCopy = isAdmin && isDuePending(migration) && !running;
  const showAction = canRerun || canRunCopy;
  const busyLabel = 'Archiving…';
  const actionLabel = canRerun ? 'Re-archive' : 'Archive now';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <OneDriveMigrationBadge status={migration.Status} />
        <button
          type="button"
          onClick={() => onViewShared(migration)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:underline"
        >
          <Share2 className="h-3.5 w-3.5" aria-hidden /> Shared
        </button>
        {running && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-darkGrey/70" title="Archiving in the background; the scheduled run will finish it.">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lightBlue" aria-hidden /> Archiving…
          </span>
        )}
        {showAction && (
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={run.isPending}
            className="rounded border border-navy/30 bg-white px-2 py-0.5 text-xs font-medium text-navy hover:bg-bg disabled:opacity-50"
          >
            {run.isPending ? busyLabel : actionLabel}
          </button>
        )}
      </div>
      {run.isError && (
        <span role="alert" className="text-xs text-red">
          {run.error instanceof ApiError ? run.error.message : 'Archive failed.'}
        </span>
      )}
      {run.data && !run.data.RanSync && (
        // The processor ran but did nothing (Graph/Vault not configured) — say so
        // rather than leaving the row silently Pending, which reads as "stuck".
        <span className="text-xs text-orange">Archive skipped — Graph/Vault not configured.</span>
      )}
      {run.data && run.data.RanSync && run.data.InProgress > 0 && (
        // On-demand hit its time budget mid-copy — the drive is large; the copy is
        // running server-side and the scheduled run will finish it. Not an error.
        <span className="text-xs text-darkGrey/70">Archiving — it&rsquo;s large, so the scheduled run will finish it.</span>
      )}
    </div>
  );
}

/** Split a root-relative item path into its containing folder and the item name.
 * "Desktop/Sub/report.xlsx" → { folder: "Desktop/Sub", name: "report.xlsx" };
 * a root-level item ("report.xlsx") → { folder: "/", name: "report.xlsx" }. */
export function splitItemPath(path: string): { folder: string; name: string } {
  const i = path.lastIndexOf('/');
  return i < 0 ? { folder: '/', name: path } : { folder: path.slice(0, i) || '/', name: path.slice(i + 1) };
}

/**
 * Read-only "Shared with" manifest for a leaver's OneDrive migration
 * (GET .../{personId}/shared-items) — the item, its containing folder, who (or
 * the sharing-link scope), and the permission role. This is a durable record, NOT
 * a live permission (PR-33 decision 3 — Oscar never re-creates sharing on the
 * Vault copy).
 */
function SharedItemsDialog({ migration, onClose }: { migration: OneDriveMigration; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['onedrive-shared-items', migration.PersonId],
    queryFn: ({ signal }) => getOneDriveSharedItems(migration.PersonId, signal),
  });

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,720px)] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-5 shadow-xl ring-1 ring-black/5 focus:outline-none">
          <Dialog.Title className="font-heading text-lg font-bold text-navy">
            OneDrive shared items — <span className="font-mono">{migration.Username ?? migration.DisplayName ?? `#${migration.PersonId}`}</span>
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-xs text-darkGrey/70">
            Who this person had shared OneDrive content with, recorded at migration time. This is a record for
            reference only — access was <span className="font-semibold">not</span> re-created on the Vault copy.
          </Dialog.Description>

          <div className="mt-3 max-h-[60vh] overflow-auto">
            {query.isLoading && <p className="py-4 text-center text-sm text-darkGrey/60">Loading…</p>}
            {query.isError && <p className="py-4 text-center text-sm text-red">Could not load shared items.</p>}
            {query.data && query.data.length === 0 && (
              <p className="py-4 text-center text-sm text-darkGrey/60">No shared items recorded.</p>
            )}
            {query.data && query.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
                  <tr>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2">Folder</th>
                    <th className="px-3 py-2">Shared with</th>
                    <th className="px-3 py-2">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.map((s) => {
                    const { folder, name } = splitItemPath(s.ItemPath);
                    return (
                    <tr key={s.OneDriveMigrationSharedItemId} className="border-t border-border">
                      <td className="px-3 py-2">
                        <span className="text-darkGrey/90">{name}</span>
                        <span className="ml-2 text-xs text-darkGrey/50">{s.ItemType}</span>
                      </td>
                      {/* Containing folder path (root-relative), so the item's location is scannable. */}
                      <td className="px-3 py-2 font-mono text-xs text-darkGrey/70">{folder}</td>
                      <td className="px-3 py-2 text-darkGrey/80">
                        {s.SharedWithDisplayName ?? s.SharedWithUpn ?? (s.LinkScope ? `${s.LinkScope} link` : '—')}
                      </td>
                      <td className="px-3 py-2 text-darkGrey/80">{s.PermissionRole}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="mt-5 flex justify-end">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
              >
                Close
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Role-assignable groups the group-removal runbook skipped, shown at the top of
 * the History dialog. While the warning is open it lists the groups and (for
 * admin/helpdesk) offers Acknowledge — the ONLY way to clear it, because the
 * runbook never revisits a finished leaver and Oscar cannot read live group
 * membership. Once acknowledged it stays as a record (who / when).
 */
function ManualRemovalPanel({ userId, username, info }: { userId: number; username: string; info: ManualGroupRemovalInfo }) {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const ack = useMutation({
    mutationFn: () => acknowledgeRoleAssignableRemoval(userId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['offboarding-status'] }),
        queryClient.invalidateQueries({ queryKey: ['offboarding-trail', userId] }),
      ]);
      setConfirming(false);
    },
  });

  return (
    <div
      role={info.Required ? 'alert' : 'note'}
      className="mb-3 rounded border border-orange/50 bg-navy/5 px-3 py-2 text-xs text-darkGrey"
    >
      <p className="flex items-center gap-1.5 text-sm font-semibold text-navy">
        <AlertTriangle className="h-4 w-4 text-orange" aria-hidden />
        {info.Required ? 'Manual group removal required' : 'Manual group removal acknowledged'}
      </p>
      <p className="mt-1">
        The runbook cannot edit role-assignable groups.{' '}
        {info.Required
          ? 'Remove the user from these groups in Entra, then acknowledge:'
          : 'These groups were removed by hand:'}
      </p>
      <ul className="mt-1 list-disc pl-5 font-mono text-navy">
        {info.Groups.map((g) => <li key={g}>{g}</li>)}
      </ul>
      {!info.Required && info.AcknowledgedDate && (
        <p className="mt-1 text-darkGrey/70">
          Acknowledged by {info.AcknowledgedBy ?? 'unknown'} on {formatDateTime(info.AcknowledgedDate)}.
        </p>
      )}
      {info.Required && canMutate && (
        <button
          type="button"
          onClick={() => { ack.reset(); setConfirming(true); }}
          className="mt-2 rounded bg-navy px-3 py-1 text-xs font-semibold text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
        >
          Acknowledge removal
        </button>
      )}
      {info.Required && !canMutate && (
        <p className="mt-1 text-darkGrey/70">Admin or helpdesk can acknowledge once removed.</p>
      )}
      <AlertDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Confirm manual removal"
        description={`Confirm ${username} has been removed from the group(s) listed. This clears the warning and is recorded in the audit log.`}
        confirmLabel={ack.isPending ? 'Saving…' : 'Acknowledge'}
        confirmDisabled={ack.isPending}
        closeOnConfirm={false}
        onConfirm={() => ack.mutate()}
      >
        {ack.isError && (
          <p role="alert" className="text-xs text-red">
            {ack.error instanceof ApiError ? ack.error.message : 'Could not record the acknowledgement.'}
          </p>
        )}
      </AlertDialog>
    </div>
  );
}

/**
 * Read-only per-user audit trail (GET /api/offboarding/status/{userId}) —
 * merges on-prem Oscar_AuditLog events, Oscar's own email-divert results, and
 * the OneDrive-to-Vault migration state (matched to the leaver by username,
 * client-side), newest first, each with its status and message (e.g. an EXO
 * failure reason, or the Vault copy result). Shown in the History dialog here
 * and on the person's Leaving tab.
 */
export function OffboardingTrail({
  userId,
  username,
  migration,
}: {
  userId: number;
  username: string;
  migration: OneDriveMigration | undefined;
}) {
  const query = useQuery({
    queryKey: ['offboarding-trail', userId],
    queryFn: ({ signal }) => getOffboardingStatusTrail(userId, signal),
  });
  const manual = query.data?.ManualGroupRemoval ?? null;

  // Merge the on-prem/Oscar trail with the OneDrive migration events and order
  // newest-first. All three sources store naive timestamps, so a uniform parse
  // reproduces the server's ordering (see backend GetUserTrailAsync).
  const events: TrailEventView[] = [
    ...(query.data?.Events ?? []),
    ...buildOneDriveEvents(migration),
  ].sort((a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime());

  return (
    <>
      {query.isLoading && <p className="py-4 text-center text-sm text-darkGrey/60">Loading…</p>}
      {query.isError && (
        <p className="mb-2 rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-darkGrey">
          Could not load on-premises / email-divert history{migration ? ' — showing the OneDrive migration only' : ''}.
        </p>
      )}
      {query.data?.MiUnavailable && (
        <p className="mb-2 rounded border border-orange/30 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
          On-premises events are temporarily unavailable — showing Oscar-native events only.
        </p>
      )}
      {manual && <ManualRemovalPanel userId={userId} username={username} info={manual} />}
      {!query.isLoading && events.length === 0 && (
        <p className="py-4 text-center text-sm text-darkGrey/60">No recorded events.</p>
      )}
      {events.length > 0 && (
        <ul className="space-y-2">
          {events.map((e, i) => (
            <li key={i} className="rounded border border-border bg-bg/40 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-navy">{e.Action}</span>
                <span className="flex items-center gap-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(e.Status)}`}>
                    {e.Status ?? '—'}
                  </span>
                  <span className="rounded bg-darkGrey/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-darkGrey/70">
                    {sourceLabel[e.Source]}
                  </span>
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 text-xs text-darkGrey/70">
                <span>{e.Message ?? ''}</span>
                {/* On-prem Oscar_AuditLog.LogDateTime is Sydney wall-clock (show verbatim);
                    Oscar's own events (email divert, OneDrive) store UTC → formatDateTime. */}
                <span className="shrink-0">{e.Source === 'OnPrem' ? formatWallClock(e.Timestamp) : formatDateTime(e.Timestamp)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function TrailDialog({
  row,
  migration,
  onClose,
}: {
  row: OffboardingStatusRow;
  migration: OneDriveMigration | undefined;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,640px)] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-5 shadow-xl ring-1 ring-black/5 focus:outline-none">
          <Dialog.Title className="font-heading text-lg font-bold text-navy">
            Offboarding history — <span className="font-mono">{row.Username}</span>
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-xs text-darkGrey/70">
            On-premises AD / OnsiteLive events, Oscar&rsquo;s email-divert result, and the OneDrive
            migration, newest first.
          </Dialog.Description>

          <div className="mt-3 max-h-[60vh] overflow-auto">
            <OffboardingTrail userId={row.UserId} username={row.Username} migration={migration} />
          </div>

          <div className="mt-5 flex justify-end">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
              >
                Close
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
